import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { Class, Student, LearningPath, Teacher, School } from '../../data/types';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';

const ClassDetailsScreen: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('students');
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [showStudentDetailsModal, setShowStudentDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  
  // Fetch data from services
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classesData, studentsData, teachersData, schoolsData, learningPathsData] = await Promise.all([
          dataService.getClasses(),
          dataService.getStudents(),
          dataService.getTeachers(),
          dataService.getSchools(),
          dataService.getLearningPaths()
        ]);
        
        // Filter classes by teacher ID - in a real app, this would be the current user's ID
        const teacherClasses = classesData.filter(cls => 
          cls.teacherId === 'teacher-001' || 
          (cls.teachers && cls.teachers.includes('teacher-001'))
        );
        
        setClasses(teacherClasses);
        setStudents(studentsData);
        setTeachers(teachersData);
        setSchools(schoolsData);
        setLearningPaths(learningPathsData);
        
        // Find the current class from the URL parameter
        const currentClass = teacherClasses.find(c => c.id === classId) || teacherClasses[0];
        setSelectedClass(currentClass);
        
        // Get students for the selected class
        if (currentClass) {
          const filteredStudents = studentsData.filter(student => 
            currentClass.students.includes(student.id)
          );
          setClassStudents(filteredStudents);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [classId]);
  
  // Handle class change from dropdown
  const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newClassId = e.target.value;
    navigate(`/classes/${newClassId}`);
    
    // Update sidebar highlight (using custom event)
    const event = new CustomEvent('classSelected', { 
      detail: { classId: newClassId } 
    });
    window.dispatchEvent(event);
  };
  
  // Calculate overall progress for each student (average of all standards)
  const calculateProgress = (student: Student) => {
    const progressValues = Object.values(student.progress);
    if (progressValues.length === 0) return 0;
    
    const completedCount = progressValues.filter(p => 
      p.learningPhaseStatus === 'Completed'
    ).length;
    
    const practiceCompletedCount = progressValues.filter(p => 
      p.learningPhaseStatus === 'PracticeCompleted'
    ).length;
    
    const videoCompletedCount = progressValues.filter(p => 
      p.learningPhaseStatus === 'VideoCompleted'
    ).length;
    
    // Weight completed standards higher
    const weightedScore = (completedCount * 100 + practiceCompletedCount * 66 + videoCompletedCount * 33) / progressValues.length;
    return Math.min(Math.round(weightedScore), 100);
  };

  // Get current learning path for a student
  const getCurrentLearningPath = (student: Student) => {
    if (student.classes.length === 0 || Object.keys(student.progress).length === 0) {
      return 'Not Assigned';
    }
    
    // Find the class and its learning paths
    const studentClass = classes.find(c => c.id === student.classes[0]);
    if (!studentClass || studentClass.learningPaths.length === 0) return 'Not Assigned';
    
    // Check for learning path assigned directly to student first
    const studentLearningPath = learningPaths.find(path => 
      path.assignedTo.some(assignment => 
        assignment.assignedToType === 'Student' && 
        assignment.assignedToId === student.id
      )
    );
    
    if (studentLearningPath) {
      return studentLearningPath.name;
    }
    
    // Otherwise, return the first learning path assigned to the class
    const classLearningPath = learningPaths.find(path => 
      path.id === studentClass.learningPaths[0]
    );
    
    return classLearningPath ? classLearningPath.name : 'Not Assigned';
  };
  
  // Get all teachers for a class
  const getTeacherNames = (cls: Class) => {
    if (!cls.teachers || cls.teachers.length === 0) {
      const teacher = teachers.find(t => t.id === cls.teacherId);
      return teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unknown Teacher';
    }
    
    return cls.teachers.map(teacherId => {
      const teacher = teachers.find(t => t.id === teacherId);
      return teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unknown Teacher';
    }).join(', ');
  };
  
  // Get school name for a class
  const getSchoolName = (schoolId: string) => {
    const school = schools.find(s => s.id === schoolId);
    return school ? school.name : 'Unknown School';
  };
  
  // Get status badge variant based on class status
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'warning';
      case 'archived':
        return 'secondary';
      default:
        return 'primary';
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 mx-auto text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-3 text-gray-600">Loading class details...</p>
        </div>
      </div>
    );
  }
  
  if (!selectedClass) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium text-gray-600">No class selected</h2>
        <p className="mt-2 text-gray-500">Please select a class from the sidebar.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{selectedClass.name}</h1>
            <Badge variant={getStatusBadgeVariant(selectedClass.status)}>
              {selectedClass.status.charAt(0).toUpperCase() + selectedClass.status.slice(1)}
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">
            Grade {selectedClass.grade} • {selectedClass.schoolYear} • {getSchoolName(selectedClass.schoolId)}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <select 
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedClass.id}
            onChange={handleClassChange}
          >
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>{cls.name} (Grade {cls.grade})</option>
            ))}
          </select>
          
          <Button variant="primary">Edit Class</Button>
        </div>
      </div>
      
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Teachers</h3>
            <p className="text-lg font-semibold">{getTeacherNames(selectedClass)}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Students</h3>
            <p className="text-lg font-semibold">{selectedClass.students.length}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Learning Paths</h3>
            <p className="text-lg font-semibold">{selectedClass.learningPaths.length}</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Average Progress</h3>
            <p className="text-lg font-semibold">
              {classStudents.length > 0 
                ? `${Math.round(classStudents.reduce((sum, student) => sum + calculateProgress(student), 0) / classStudents.length)}%` 
                : 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="border-b border-gray-200 mb-5">
          <nav className="flex space-x-8">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'students'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('students')}
            >
              Students
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'learning-paths'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('learning-paths')}
            >
              Learning Paths
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </nav>
        </div>
        
        <div>
          {activeTab === 'students' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-medium text-gray-900">Students ({classStudents.length})</h2>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm">
                    Add Students
                  </Button>
                  <Button variant="secondary" size="sm">
                    Export
                  </Button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Learning Path
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progress
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Activity
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classStudents.map((student) => (
                      <tr key={student.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-600 font-medium">
                                  {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {student.firstName} {student.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                Student ID: {student.id.split('-')[1]}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{getCurrentLearningPath(student)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-primary h-2.5 rounded-full" 
                              style={{ width: `${calculateProgress(student)}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {calculateProgress(student)}% Complete
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Object.values(student.progress).length > 0 
                            ? new Date(Math.max(...Object.values(student.progress).map(p => new Date(p.lastActivity).getTime()))).toLocaleDateString() 
                            : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            className="text-primary hover:text-primary-700"
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowStudentDetailsModal(true);
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {classStudents.length === 0 && (
                <div className="text-center py-10">
                  <svg 
                    className="mx-auto h-12 w-12 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1} 
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" 
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No students in this class</h3>
                  <p className="mt-1 text-sm text-gray-500">Add students to get started.</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'learning-paths' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-medium text-gray-900">Learning Paths</h2>
                <Button variant="secondary" size="sm">
                  Assign Learning Path
                </Button>
              </div>
              
              {selectedClass.learningPaths.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedClass.learningPaths.map(pathId => {
                    const path = learningPaths.find(p => p.id === pathId);
                    return path ? (
                      <div key={path.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">{path.name}</h3>
                            <p className="text-sm text-gray-500">Grade {path.grade}</p>
                          </div>
                          <Badge variant="primary">{path.difficulty}</Badge>
                        </div>
                        <p className="text-gray-600 mb-4 line-clamp-2">{path.description}</p>
                        <div className="flex justify-between items-center">
                          <div className="text-sm text-gray-500">
                            {path.standards.length} Standards
                          </div>
                          <Button variant="secondary" size="sm">
                            View Details
                          </Button>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              ) : (
                <div className="text-center py-10">
                  <svg 
                    className="mx-auto h-12 w-12 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1} 
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No learning paths assigned</h3>
                  <p className="mt-1 text-sm text-gray-500">Assign a learning path to get started.</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Class Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Average Completion</h4>
                    <div className="text-3xl font-bold text-gray-900">
                      {classStudents.length > 0 
                        ? `${Math.round(classStudents.reduce((sum, student) => sum + calculateProgress(student), 0) / classStudents.length)}%` 
                        : 'N/A'}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Active Students</h4>
                    <div className="text-3xl font-bold text-gray-900">
                      {classStudents.filter(s => 
                        Object.values(s.progress).some(p => 
                          new Date(p.lastActivity) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                        )
                      ).length} / {classStudents.length}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Standards Mastered</h4>
                    <div className="text-3xl font-bold text-gray-900">
                      {classStudents.reduce(
                        (sum, student) => sum + Object.values(student.progress).filter(p => p.learningPhaseStatus === 'Completed').length, 
                        0
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Progress Distribution</h4>
                    <div className="h-40 bg-gray-100 rounded-lg flex items-end justify-around px-4">
                      {[0, 1, 2, 3, 4].map(level => {
                        const min = level * 20;
                        const max = (level + 1) * 20;
                        const count = classStudents.filter(s => {
                          const progress = calculateProgress(s);
                          return progress >= min && progress < max;
                        }).length;
                        const height = classStudents.length > 0 ? (count / classStudents.length) * 100 : 0;
                        
                        return (
                          <div key={level} className="flex flex-col items-center">
                            <div 
                              className="w-12 bg-primary rounded-t-sm" 
                              style={{ height: `${Math.max(height, 5)}%` }}
                            ></div>
                            <div className="text-xs text-gray-500 mt-2">{min}-{max}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Class Statistics</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex justify-between">
                        <span className="text-gray-600">Standards Completed:</span>
                        <span className="font-medium">12 of 18</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600">Highest Mastery:</span>
                        <span className="font-medium">Addition (93%)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600">Needs Improvement:</span>
                        <span className="font-medium">Fractions (42%)</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600">Most Attempts:</span>
                        <span className="font-medium">Division</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Students Requiring Intervention</h3>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classStudents.filter(s => calculateProgress(s) < 50).slice(0, 3).map((student, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{student.firstName} {student.lastName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{calculateProgress(student)}%</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{idx === 0 ? '3 days ago' : idx === 1 ? '5 days ago' : '1 week ago'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button className="text-primary hover:text-primary-700">Contact</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                <div className="flex justify-end mt-6">
                  <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700">
                    Download Full Report
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'settings' && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-500">Class settings will be displayed here</h3>
              <p className="mt-2 text-sm text-gray-500">Note: Classes cannot be edited or deleted as they are imported from an external system.</p>
            </div>
          )}
        </div>
      </Card>

      {/* Student Details Modal */}
      {showStudentDetailsModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Student Details
              </h2>
              <button
                onClick={() => setShowStudentDetailsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Student Profile */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex flex-col items-center mb-4">
                  <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center mb-3">
                    <span className="text-gray-600 font-medium text-2xl">
                      {selectedStudent.firstName.charAt(0)}{selectedStudent.lastName.charAt(0)}
                    </span>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">{selectedStudent.firstName} {selectedStudent.lastName}</h3>
                  <p className="text-sm text-gray-500">Student ID: {selectedStudent.id.split('-')[1]}</p>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Grade:</span>
                    <span className="font-medium">{selectedStudent.grade}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">School:</span>
                    <span className="font-medium">{selectedStudent.school.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{selectedStudent.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Username:</span>
                    <span className="font-medium">{selectedStudent.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Classes:</span>
                    <span className="font-medium">{selectedStudent.classes.length}</span>
                  </div>
                </div>
              </div>

              {/* Learning Progress */}
              <div className="md:col-span-2 bg-white p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Learning Progress</h3>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                    <span className="text-sm font-medium text-gray-700">{calculateProgress(selectedStudent)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-primary h-2.5 rounded-full" 
                      style={{ width: `${calculateProgress(selectedStudent)}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-md font-medium text-gray-800 mb-2">Current Learning Path</h4>
                  <p className="text-sm text-gray-600">{getCurrentLearningPath(selectedStudent)}</p>
                </div>
                
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-2">Standards Progress</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standard</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accuracy</th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(selectedStudent.progress).map(([standardId, progress]) => (
                          <tr key={standardId}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {standardId.substring(0, 8)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                progress.learningPhaseStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                                progress.learningPhaseStatus === 'PracticeCompleted' ? 'bg-blue-100 text-blue-800' :
                                progress.learningPhaseStatus === 'VideoCompleted' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {progress.learningPhaseStatus}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                              {progress.practiceAccuracy ? `${progress.practiceAccuracy}%` : 'N/A'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                              {new Date(progress.lastActivity).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                        {Object.keys(selectedStudent.progress).length === 0 && (
                          <tr>
                            <td colSpan={4} className="px-3 py-4 text-sm text-gray-500 text-center">
                              No progress data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Achievements */}
              <div className="md:col-span-3 bg-white p-4 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Achievements</h3>
                
                {selectedStudent.achievements.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {selectedStudent.achievements.map((achievement) => (
                      <div key={achievement.id} className="border border-gray-200 rounded-lg p-3 flex items-center space-x-3">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{achievement.name}</p>
                          <p className="text-xs text-gray-500">{achievement.points} points</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No achievements earned yet</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowStudentDetailsModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetailsScreen;
